// Pure view helpers for SpatialMap — everything computable lives here (unit-tested in
// spatialMapView.test.ts) so the component file stays thin. No maplibre / DOM-library imports:
// this module is eager-safe and runs in Node under vitest.

import type { SpatialGeoJson } from '../decode/types';
import { SPATIAL_STYLE_DEFAULTS } from './mapThemes';
import type { PlanarTransform, SpatialMapFeature, SpatialMapLayerInput } from './spatialMapTypes';

export type BoundingBox = [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]

// --- fit bounds --------------------------------------------------------------

function walkCoords(coords: unknown, visit: (lng: number, lat: number) => void): void {
  if (!Array.isArray(coords)) return;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    visit(coords[0] as number, coords[1] as number);
    return;
  }
  for (const c of coords) walkCoords(c, visit);
}

function visitGeometry(g: SpatialGeoJson, visit: (lng: number, lat: number) => void): void {
  if (g.type === 'GeometryCollection') {
    for (const child of g.geometries) visitGeometry(child, visit);
    return;
  }
  walkCoords(g.coordinates, visit);
}

/** Envelope of mixed geometries as `[minLng, minLat, maxLng, maxLat]`, or `null` if empty. */
export function computeFitBounds(features: SpatialMapFeature[]): BoundingBox | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const f of features) {
    visitGeometry(f.geometry.geojson, (lng, lat) => {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    });
  }
  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
  return [minLng, minLat, maxLng, maxLat];
}

/** Structural slice of maplibre's Map used by `fitMapToLayers` (keeps this module maplibre-free). */
export type FitBoundsMap = {
  fitBounds: (
    bounds: [[number, number], [number, number]],
    options: { padding: number; animate: boolean; maxZoom: number },
  ) => unknown;
};

/**
 * Fit the view to every feature of every given layer (planar-aware). Shared by
 * the controller's load-time fit and the map's zoom-to-fit control, so both
 * land on the identical extent. `animate` defaults off (load-time); the button
 * passes true for a user-gesture transition. No-op when there is nothing to fit.
 */
export function fitMapToLayers(
  map: FitBoundsMap,
  layers: SpatialMapLayerInput[],
  planarTransform: PlanarTransform | null | undefined,
  options?: { animate?: boolean },
): void {
  const b = computeFitBounds(layers.flatMap((l) => l.features));
  if (!b) return;
  const fwd = planarTransform?.forward;
  const min = fwd ? fwd([b[0], b[1]]) : [b[0], b[1]];
  const max = fwd ? fwd([b[2], b[3]]) : [b[2], b[3]];
  map.fitBounds([[min[0], min[1]], [max[0], max[1]]], {
    padding: 32,
    animate: options?.animate ?? false,
    maxZoom: 14,
  });
}

// --- feature collection cap --------------------------------------------------

export type GeoJsonFeature = {
  type: 'Feature';
  id: string | number;
  properties: Record<string, unknown>;
  geometry: SpatialGeoJson;
};
export type GeoJsonFeatureCollection = { type: 'FeatureCollection'; features: GeoJsonFeature[] };
export type BuiltFeatureCollection = {
  collection: GeoJsonFeatureCollection;
  truncated: boolean;
  total: number;
};

/** Builds a GeoJSON FeatureCollection capped at `featureCap`, reporting truncation + total. */
export function buildFeatureCollection(
  features: SpatialMapFeature[],
  featureCap: number,
): BuiltFeatureCollection {
  const total = features.length;
  const cap = Math.max(0, Math.trunc(featureCap));
  const kept = total > cap ? features.slice(0, cap) : features;
  return {
    collection: {
      type: 'FeatureCollection',
      features: kept.map((f) => ({
        type: 'Feature',
        id: f.id,
        properties: { ...(f.properties ?? {}), __featureId: f.id },
        geometry: f.geometry.geojson,
      })),
    },
    truncated: total > cap,
    total,
  };
}

// --- cluster buckets ---------------------------------------------------------

/** <100 → radii[0], <1000 → radii[1], ≥1000 → radii[2]. */
export function clusterBucketRadius(count: number): number {
  const [small, medium, large] = SPATIAL_STYLE_DEFAULTS.clusterRadii;
  if (count < 100) return small;
  if (count < 1000) return medium;
  return large;
}

// --- webgl probe -------------------------------------------------------------

export type CanvasFactory = () => HTMLCanvasElement;

/** True if a WebGL(2) context is obtainable. Injectable canvas factory keeps it unit-testable. */
export function webglProbe(createCanvas?: CanvasFactory): boolean {
  const make: CanvasFactory | null =
    createCanvas ??
    (typeof document !== 'undefined'
      ? () => document.createElement('canvas')
      : null);
  if (!make) return false;
  try {
    const canvas = make();
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

// --- custom-style URL safety (A18/NG3) --------------------------------------

/**
 * Accepts `http`/`https` URLs ONLY; rejects other schemes and any URL carrying userinfo
 * (`user:pass@` — can smuggle credentials). Never logs the URL (it may carry an API key).
 */
export function isSafeStyleUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
  if (parsed.username !== '' || parsed.password !== '') return false;
  return true;
}

// --- WebGL-unavailable panel summary ----------------------------------------

/** Compact human summary of the features (used by the WebGL-off fallback panel). */
export function formatFeatureSummary(features: SpatialMapFeature[]): string {
  if (features.length === 0) return 'No geometry to display';
  if (features.length === 1) {
    const g = features[0].geometry;
    return `1 ${g.geomType} (${g.pointCount} point${g.pointCount === 1 ? '' : 's'})`;
  }
  return `${features.length} features`;
}
